/**
 * DEV-1991 (эпик DEV-1980). Задачи CRM — один источник на сделки, клиентов и
 * агентов (ADR-0055).
 *
 * Раньше карточка сделки ходила в deals, а карточка клиента — в client-data;
 * обе ручки выпилены в О1/О2, и без этого слоя задачи в карточках просто не
 * грузятся. Сущность-владелец теперь параметр, а не отдельный сервис.
 *
 * Доменные модели (`DealTask`, `ClientTask`) не трогаем: они описывают, что
 * карточка показывает, и переезд источника их не меняет.
 */
import { apiClient, tasksApi } from '@/api'
import type {
  TasksTask,
  TasksCreateTaskRequest,
  TasksServiceUpdateTaskBody,
  TasksTaskSortField,
  TasksTaskStatus,
} from '@/api/clients/tasks'

/** Сущность-владелец задачи. Совпадает с enum TaskEntityType сервиса задач. */
export const TASK_ENTITY_DEAL = 'TASK_ENTITY_TYPE_DEAL'
export const TASK_ENTITY_CLIENT = 'TASK_ENTITY_TYPE_CLIENT'

export type TaskEntity = typeof TASK_ENTITY_DEAL | typeof TASK_ENTITY_CLIENT

/** Статусы задачи в терминах сервиса. Карточкам хватает «закрыта / не закрыта». */
const STATUS_TODO = 'TASK_STATUS_TODO'
const STATUS_DONE = 'TASK_STATUS_DONE'

/**
 * Потолок выдачи. У сущности задач десятки, а не тысячи; сервис отдаёт страницу,
 * и без явного размера карточка получила бы дефолтную (20) и молча потеряла бы
 * хвост списка.
 */
const PER_PAGE = '200'

/**
 * DEV-1992. Фильтр борды задач. Поля — те же, что у RPC, но списками строк:
 * борда собирает их из URL, а не из формы.
 */
export interface TaskListQuery {
  assigneeIds?: string[]
  authorIds?: string[]
  taskTypeIds?: string[]
  statuses?: TasksTaskStatus[]
  dueFrom?: string
  dueTo?: string
  createdFrom?: string
  createdTo?: string
  completedFrom?: string
  completedTo?: string
  overdue?: boolean
  sortBy?: TasksTaskSortField
  sortDesc?: boolean
}

/**
 * DEV-2023. Участник задач: тот, кто встречается в них исполнителем или автором.
 * ФИО здесь нет — сервис задач пользователей не знает, имя резолвит
 * user-directory по идентификатору (DEV-395).
 */
export interface TaskPerson {
  managerId: string
  assigned: number
  authored: number
}

/** Счётчики приезжают из gateway строками: protojson так отдаёт int64. */
interface TaskPeopleResponse {
  people?: Array<{
    managerId?: string
    assigned?: string | number
    authored?: string | number
  }>
}

export interface TaskListPage {
  tasks: TasksTask[]
  /** Сколько задач под фильтром всего — включая непрочитанный хвост. */
  total: number
  /** Хвост не влез в потолок выдачи: борда обязана сказать об этом вслух. */
  truncated: boolean
}

/**
 * Потолок борды. Канбан и группировки считаются на клиенте по всей выборке,
 * поэтому страницы дочитываем до конца — но не бесконечно: без потолка фильтр
 * «всё за всё время» утащил бы в браузер всю таблицу.
 */
const BOARD_MAX_PAGES = 5

/** Одна страница кросс-сущностного списка: общий вызов борды и счётчиков. */
const listPage = (query: TaskListQuery, page: number, perPage: string) =>
  tasksApi.tasksServiceListTasks(
    undefined, // entityType — борда собирает задачи всех сущностей
    undefined, // entityId
    query.assigneeIds?.length ? query.assigneeIds : undefined,
    query.authorIds?.length ? query.authorIds : undefined,
    query.statuses?.length ? query.statuses : undefined,
    query.taskTypeIds?.length ? query.taskTypeIds : undefined,
    undefined, // withoutType — «Без типа» показываем группой, а не фильтром
    query.dueFrom,
    query.dueTo,
    query.createdFrom,
    query.createdTo,
    query.completedFrom,
    query.completedTo,
    query.overdue,
    query.sortBy,
    query.sortDesc,
    String(page),
    perPage,
  )

export const tasksServiceApi = {
  /** Задачи одной сущности — то, что показывает её карточка. */
  async listByEntity(entityType: TaskEntity, entityId: string): Promise<TasksTask[]> {
    const { data } = await tasksApi.tasksServiceListTasks(
      entityType,
      entityId,
      undefined, // assigneeIds
      undefined, // authorIds
      undefined, // statuses — карточке нужны и открытые, и закрытые
      undefined, // taskTypeIds
      undefined, // withoutType
      undefined, // dueFrom
      undefined, // dueTo
      undefined, // createdFrom
      undefined, // createdTo
      undefined, // completedFrom
      undefined, // completedTo
      undefined, // overdue
      undefined, // sortBy
      undefined, // sortDesc
      undefined, // paginationPage
      PER_PAGE,
    )

    return data.tasks ?? []
  },

  /**
   * DEV-1992. Кросс-сущностный список для борды: без entity_type, то есть
   * задачи сделок, клиентов и агентов вперемешку. Чужие задачи сервис отдаёт
   * только носителю calendar:read_all — сужать выдачу здесь нечем и не нужно.
   */
  async list(query: TaskListQuery): Promise<TaskListPage> {
    const tasks: TasksTask[] = []
    let total = 0

    for (let page = 1; page <= BOARD_MAX_PAGES; page += 1) {
      const { data } = await listPage(query, page, PER_PAGE)

      tasks.push(...(data.tasks ?? []))
      total = Number(data.pagination?.total ?? tasks.length) || tasks.length

      if (tasks.length >= total || !data.tasks?.length) break
    }

    return { tasks, total, truncated: tasks.length < total }
  },

  /**
   * DEV-2429. Сколько задач под фильтром — без самих задач: дашборду нужна
   * цифра просрочки, а не сотни строк ради неё.
   */
  async count(query: TaskListQuery): Promise<number> {
    const { data } = await listPage(query, 1, '1')

    return Number(data.pagination?.total ?? 0)
  },

  /**
   * DEV-2023. Кто встречается в задачах — из этого списка борда строит фильтры
   * «Исполнители» и «Авторы».
   *
   * Раньше их строил справочник менеджеров (роли manager/hos/ceo), и задачи
   * саппорта, админов и уволенных отобрать было нечем: на проде так выпадали
   * 93 задачи из 371.
   *
   * Прямым axios, а не сгенерированным клиентом: регенерация сдвигает
   * позиционные аргументы всех методов разом (DEV-1993), и ради одной ручки
   * этого не стоит. Тот же приём, что у useManagers с auth users/by-roles.
   * Заменить на сгенерированный метод при ближайшей общей регенерации.
   */
  async people(): Promise<TaskPerson[]> {
    const { data } = await apiClient.get<TaskPeopleResponse>('/api/v1/tasks/people')

    return (data.people ?? [])
      .filter((person) => !!person.managerId)
      .map((person) => ({
        managerId: person.managerId as string,
        assigned: Number(person.assigned ?? 0),
        authored: Number(person.authored ?? 0),
      }))
  },

  async create(body: TasksCreateTaskRequest): Promise<TasksTask> {
    const { data } = await tasksApi.tasksServiceCreateTask(body)

    return data.task ?? {}
  },

  async update(id: string, body: TasksServiceUpdateTaskBody): Promise<TasksTask> {
    const { data } = await tasksApi.tasksServiceUpdateTask(id, body)

    return data.task ?? {}
  },

  async remove(id: string): Promise<void> {
    await tasksApi.tasksServiceDeleteTask(id)
  },
}

/** Статус по галке «выполнено» — карточки говорят именно так. */
export function statusFor(completed: boolean): typeof STATUS_TODO | typeof STATUS_DONE {
  return completed ? STATUS_DONE : STATUS_TODO
}

/**
 * Закрыта ли задача. Смотрим и на статус, и на отметку о закрытии: у задач,
 * приехавших импортом из зеркал (DEV-1985), статус выводился из completed_at.
 */
export function isCompleted(dto: TasksTask): boolean {
  return dto.status === STATUS_DONE || !!dto.completedAt
}
